import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MOTIF_MODAL_DATA,
  MotifButtonModule,
  MotifCardModule,
  MotifFormsModule,
  MotifMenuModule,
  MotifModal,
  MotifModalModule,
  MotifModalRef,
} from '@ey-xd/ng-motif';
import {
  CommonConstants,
  DeliverablesConstants,
} from '@sa/app/shared/constants/ApplicationConstants';
import { ApplicationUser } from '@sa/app/shared/interfaces/ApplicationUser';
import { ProcessStepResponse } from '@sa/app/shared/interfaces/ProcessStep';
import { WorkspaceTemplateItemRequest } from '@sa/app/shared/interfaces/WorkspaceTemplateItemCreateRequest';
import { AgentService } from '@sa/app/shared/services/agent-service';
import { CommonService } from '@sa/app/shared/services/common.service';
import { UserAgentService } from '@sa/app/shared/services/user-agent.service';
import { WorkspaceTemplateService } from '@sa/app/shared/services/workspace-template.service';
import { LoggerService } from '@sa/core/services/logger.service';
import { WorkspaceService } from '@sa/shared/services/workspace.service';
import { forkJoin, of } from 'rxjs';
@Component({
  selector: 'app-add-milestone-template-panel',
  standalone: true,
  imports: [
    CommonModule,
    MotifMenuModule,
    FormsModule,
    MotifCardModule,
    MotifFormsModule,
    MotifButtonModule,
    MotifModalModule,
    ReactiveFormsModule,
  ],
  templateUrl: './add-milestone-template-panel.component.html',
  styleUrl: './add-milestone-template-panel.component.scss',
})
export class AddMilestoneTemplatePanelComponent implements OnInit {
  workspaceService = inject(WorkspaceService);
  commonService = inject(CommonService);
  loggerService = inject(LoggerService);
  agentService = inject(AgentService);
  userService = inject(UserAgentService);
  public modalData = inject(MOTIF_MODAL_DATA);
  private modalRef = inject(MotifModalRef);
  private workspaceTemplateService = inject(WorkspaceTemplateService);
  workspaceTemplates$ = this.workspaceTemplateService.workspaceTemplates$;

  public AddMilestoneConstants = DeliverablesConstants.AddMilestonePanel;
  public showValidationErrors = false;
  action: any;
  tenantGuid: any;
  workspaceTemplateGuid: any;
  workspaceTemplateName: string = '';
  workspaceTemplateItemGuid: any = '';
  errorMessage: string = '';
  mileStoneData: any;
  milestoneOwnersList: Array<{ id: string; name: string }> = [];
  processStepsList: ProcessStepResponse[] = [];
  isProcessStepTrackingEnabled = false;
  isProcessStepResolutionComplete = false;
  milestoneForm = new FormGroup({
    milestoneName: new FormControl('', [
      Validators.required,
      Validators.maxLength(150),
    ]),
    description: new FormControl('', [Validators.maxLength(255)]),
    deliverable: new FormControl(this.workspaceTemplateName),
    processStep: new FormControl('', [Validators.required]),
    // milestoneOwner: new FormControl('', [Validators.required])
  });

  constructor(private modal: MotifModal) {}

  ngOnInit() {
    this.tenantGuid =
      this.commonService.getState('selectedTenantGuid') ||
      JSON.parse(sessionStorage.getItem('TenantId') || '""');
    // Get the selected template from service
    const selectedTemplate =
      this.workspaceTemplateService.getSelectedTemplate();

    if (selectedTemplate) {
      this.workspaceTemplateGuid = selectedTemplate.workspaceTemplateGuid;
      this.workspaceTemplateName = selectedTemplate.workSpaceTemplateName;
    }
    else if (this.modalData?.parentGuid) {
      this.workspaceTemplateGuid = this.modalData.parentGuid;
    }
    this.workspaceTemplateItemGuid =
      this.modalData?.workspaceTemplateItemGuid || '';
    this.action = this.modalData.action;

    this.resolveProcessStepTrackingApplicabilityAndInitialize(selectedTemplate);
  }

  private resolveProcessStepTrackingApplicabilityAndInitialize(
    selectedTemplate: any
  ): void {
    if (!this.tenantGuid || !this.workspaceTemplateGuid) {
      this.resolveProcessStepTrackingApplicability(selectedTemplate);
      this.finalizeProcessStepResolution();
      this.initializePanelData();
      return;
    }

    this.workspaceService
      .loadWorkspaceTemplateItem(this.tenantGuid, this.workspaceTemplateGuid)
      .subscribe({
        next: templateDetails => {
          const apiTrackingFlag =
            templateDetails?.data?.isProcessStepTrackingEnabled;

          if (typeof apiTrackingFlag === 'boolean') {
            this.isProcessStepTrackingEnabled = apiTrackingFlag;
          } else {
            this.resolveProcessStepTrackingApplicability(selectedTemplate);
          }

          if (!this.workspaceTemplateName && templateDetails?.data?.title) {
            this.workspaceTemplateName = templateDetails.data.title;
          }

          this.finalizeProcessStepResolution();
          this.initializePanelData();
        },
        error: () => {
          this.resolveProcessStepTrackingApplicability(selectedTemplate);
          this.finalizeProcessStepResolution();
          this.initializePanelData();
        },
      });
  }

  private finalizeProcessStepResolution(): void {
    this.updateProcessStepValidation();
    this.isProcessStepResolutionComplete = true;
  }

  private initializePanelData(): void {

    if (this.action === 'Edit') {
      forkJoin({
        users: this.userService.getApplicationUsers(this.tenantGuid),
        processSteps: this.isProcessStepTrackingEnabled
          ? this.workspaceService.getProcessSteps(this.tenantGuid)
          : of([] as ProcessStepResponse[]),
        milestone: this.workspaceService.getWorkspaceTemplateItem(
          this.tenantGuid,
          this.workspaceTemplateItemGuid
        ),
      }).subscribe(({ users, processSteps, milestone }) => {
        this.milestoneOwnersList = this.mapUsersToOwners(users);
        this.processStepsList = this.sortProcessSteps(processSteps);
        this.mileStoneData = milestone;
        this.workspaceTemplateItemGuid = milestone.workspaceTemplateItemGuid;
        this.milestoneForm.patchValue({
          milestoneName: milestone.title,
          description: milestone.description,
          deliverable: this.workspaceTemplateName,
          processStep: this.isProcessStepTrackingEnabled
            ? milestone.processStepGuid || ''
            : '',
          // milestoneOwner: milestone.assignedToUser || ''
        });
        this.milestoneForm.get('deliverable')?.disable();
      });
    } else {
      this.initializeMilestoneForm(this.action);
      this.loadMilestoneOwners();
      if (this.isProcessStepTrackingEnabled) {
        this.loadProcessSteps();
      }
    }
  }

  private resolveProcessStepTrackingApplicability(selectedTemplate: any): void {
    const modalTrackingFlag = this.modalData?.isProcessStepTrackingEnabled;
    if (typeof modalTrackingFlag === 'boolean') {
      this.isProcessStepTrackingEnabled = modalTrackingFlag;
      return;
    }

    const deliverableTypeCode =
      selectedTemplate?.deliverableType?.deliverableTypeCode
        ?.toString()
        .toUpperCase() || '';
    const isExcludedDeliverableType =
      deliverableTypeCode === CommonConstants.Defaults.GL_REC ||
      deliverableTypeCode === CommonConstants.Defaults.TRANSITION;

    this.isProcessStepTrackingEnabled = !isExcludedDeliverableType;
  }

  private updateProcessStepValidation(): void {
    const processStepControl = this.milestoneForm.get('processStep');
    if (!processStepControl) {
      return;
    }

    if (this.isProcessStepTrackingEnabled) {
      processStepControl.setValidators([Validators.required]);
    } else {
      processStepControl.clearValidators();
      processStepControl.setValue('');
    }

    processStepControl.updateValueAndValidity({ emitEvent: false });
  }

  private loadProcessSteps() {
    this.workspaceService
      .getProcessSteps(this.tenantGuid)
      .subscribe((data: ProcessStepResponse[]) => {
        this.processStepsList = this.sortProcessSteps(data);
      });
  }

  private sortProcessSteps(steps: ProcessStepResponse[]): ProcessStepResponse[] {
    return [...steps].sort(
      (a, b) => (a.processStepOrder || 0) - (b.processStepOrder || 0)
    );
  }

  private loadMilestoneOwners() {
    this.userService
      .getApplicationUsers(this.tenantGuid)
      .subscribe((data: ApplicationUser[]) => {
        this.milestoneOwnersList = this.mapUsersToOwners(data);
      });
  }

  private mapUsersToOwners(
    users: ApplicationUser[]
  ): Array<{ id: string; name: string }> {
    return users
      .filter(item => item.isActive)
      .map(item => ({
        id: item.userGuid,
        name: [item.firstName, item.lastName].filter(Boolean).join(' '),
      }));
  }

  initializeMilestoneForm(action: string) {
    if (action === 'Add') {
      this.milestoneForm = new FormGroup({
        milestoneName: new FormControl('', [
          Validators.required,
          Validators.maxLength(150),
        ]),
        description: new FormControl('', [Validators.maxLength(255)]),
        deliverable: new FormControl({
          value: this.workspaceTemplateName,
          disabled: true,
        }),
        processStep: new FormControl(
          '',
          this.isProcessStepTrackingEnabled ? [Validators.required] : []
        ),
        //milestoneOwner: new FormControl('', [Validators.required])
      });
    } else {
      this.milestoneForm.patchValue({
        milestoneName: this.mileStoneData.milestoneName,
        description: this.mileStoneData.description,
        deliverable: this.mileStoneData.deliverable,
        processStep: this.mileStoneData.processStepGuid || '',
        // milestoneOwner: this.mileStoneData.assignedToUser || ''
      });
    }
  }

  get isMilestoneNameInvalid(): boolean {
    return this.milestoneForm.get('milestoneName')?.invalid ?? false;
  }

  get isDescriptionInvalid(): boolean {
    return this.milestoneForm.get('description')?.invalid ?? false;
  }

  get isProcessStepInvalid(): boolean {
    return this.isProcessStepTrackingEnabled
      ? (this.milestoneForm.get('processStep')?.invalid ?? false)
      : false;
  }

  onCreate(): void {
    this.showValidationErrors = true;
    if (this.milestoneForm.invalid) {
      this.getErrorMessage();
      this.loggerService.logError(this.errorMessage);
      return;
    }
    const milestoneItem: WorkspaceTemplateItemRequest = {
      workspaceTemplateGuid: this.workspaceTemplateGuid,
      typeId: DeliverablesConstants.WorkspaceItemType.Milestones.id,
      title: this.milestoneForm.value.milestoneName?.trim() ?? '',
      description: this.milestoneForm.value.description ?? '',
      isActive: true,
      parentGuid: null,
      assignedTo: '',
      assignedToAgent: null,
      assignedToUser: null,
      processStepGuid: this.isProcessStepTrackingEnabled
        ? this.milestoneForm.value.processStep ?? null
        : null,
      ...(this.action === 'Edit' && {
        workspaceTemplateItemGuid: this.workspaceTemplateItemGuid,
      }),
    };

    this.workspaceService
      .createWorkspaceTemplateItem(this.tenantGuid, milestoneItem)
      .subscribe({
        next: response => {
          // Show the success message
          this.loggerService.logSuccess(
            this.action === 'Add'
              ? this.AddMilestoneConstants.Message.AddSuccess
              : this.AddMilestoneConstants.Message.EditSuccess
          );
          // After successful save in AddMilestoneTemplatePanelComponent
          this.agentService.triggerRefresh(); // Notifies listeners to refresh grid data

          // Clear selection after modal closes
          this.workspaceTemplateService.setSelectedTemplate(null);

          this.modalRef.close(); // Closes the modal
        },
        error: err => {
          if (err?.status === 422) {
            this.errorMessage =
              this.AddMilestoneConstants.Message.BusinessMilestoneConflict;
            this.showValidationErrors = true;
            this.loggerService.logApiError(this.errorMessage);
            return;
          }

          // Show the error message from the API if available
          this.errorMessage =
            err?.error?.message ||
            this.AddMilestoneConstants.Message.ErrorInCreation;
          this.loggerService.logApiError(
            this.errorMessage
          );
        },
      });
  }

  getErrorMessage() {
    if (this.milestoneForm.get('milestoneName')?.errors?.['required']) {
      this.errorMessage =
        this.AddMilestoneConstants.Message.MilestoneTitleRequired;
    }
    else if (
      this.isProcessStepTrackingEnabled &&
      this.milestoneForm.get('processStep')?.errors?.['required']
    ) {
      this.errorMessage =
        this.AddMilestoneConstants.Message.BusinessMilestoneRequired;
    }
    // else if (this.milestoneForm.get('milestoneOwner')?.errors?.['required']) {
    //   this.errorMessage = this.AddMilestoneConstants.Message.MilestoneOwnerRequired;
    // }
  }
}
